import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { overtimeExportService } from '@/api/domains/overtime/overtimeExport.service';
import { useExport } from '@/hooks/ui/useExport';
import { useUI } from '@/hooks/ui/useUI';
import type { UIExportFormat } from '@/types/fileExport.types';
import { getAPIFormat } from '@/types/fileExport.types';


export interface UseOvertimeExportParams {
  monthlyCutId: number;
  companyId?: number;
  employeeId?: number;
  type?: 'Todos' | 'HHEE' | 'Atrasos';
}

export interface UseOvertimeExportResult {
  exportOvertimeSummary: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
}

export function useOvertimeExport(
  params: UseOvertimeExportParams,
): UseOvertimeExportResult {
  const { t: translate } = useTranslation('attendance');
  const { showNotification } = useUI();

  const { onExport: exportFile, isExporting } = useExport<UIExportFormat>({
    exportFn: (format) => overtimeExportService.exportEmployeeHheeSummary({
      monthlyCutId: params.monthlyCutId,
      companyId: params.companyId,
      employeeId: params.employeeId,
      type: params.type,
      format: getAPIFormat(format),
    }),
    successMessage: translate('overtimeReport.messages.exportSuccess'),
    errorMessage: translate('overtimeReport.messages.exportError'),
  });

  const exportOvertimeSummary = useCallback(async (format: UIExportFormat) => {
    if (!params.monthlyCutId || params.monthlyCutId <= 0) {
      showNotification('error', translate('overtimeReport.messages.invalidCutoffError'), 5000);
      return;
    }
    await exportFile(format);
  }, [exportFile, params.monthlyCutId, showNotification, translate]);

  return { exportOvertimeSummary, isExporting };
}
